import { Modal } from "react-bootstrap"
import { useAiContext } from "src/context/AiContext";
import { Formik } from "formik";
import i18next from "i18next";
import { RecordTypeEnum } from "src/api/models/Enums";
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import _ from 'lodash';
import { createReportValidationSchema } from "./createReportValidationSchema";
import RequiredFormLabel from "../RequiredFormLabel/RequiredFormLabel";
import SpinnerButton from "../SpinnerButton/SpinnerButton";
import { CreateReportDto } from ".";
import { useEffect, useState } from "react";
import MultiFileUploader from "../MultiFileUploader/MultiFileUploader";
import VehicleCardBody from "../VehicleCardBody/VehicleCardBody";
import { useApiClient } from "src/hooks/useApiClient";
import { useErrorToast } from "src/hooks/useErrorToast";
import { randomString } from "src/utils";
import { Storage } from 'aws-amplify';
import { useAuth } from "src/hooks/useAuth";
import { useAccountTypes } from "src/hooks/useAccountTypes";
import toaster from "src/utils/toaster";
// @ts-ignore
import './reportForm.scss';


const SUPPORTED_FILE_TYPES = [
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
];

type CreateReportFormData = Partial<CreateReportDto & {
  vehicle: any
}>

const buildInitialValues = ({ user, signalingData }): CreateReportFormData => {
  return ({
    RecordTypeId: signalingData.accountInfo.RecordTypeId,
    RecordType: signalingData.accountInfo.RecordType,
    FirstName: signalingData.accountInfo.FirstName,
    LastName: signalingData.accountInfo.LastName,
    Company: signalingData.accountInfo.Company,
    Partita_IVA__c: '',
    Codice_Fiscale__c: '',
    Email__c: '',
    Phone: '',
    Marca__c: signalingData.vehicle.dati_veicolo.marca,
    Modello__c: signalingData.vehicle.dati_veicolo.modello,
    ID_Vehicle_HMP__c: signalingData.vehicle.id,
    Provvigioni_Segnalatore__c: signalingData.bestOffer.updated_commissioni,
    Provvigioni_passive__c: signalingData.bestOffer.commissioni,
    Noleggiatore__c: signalingData.noleggiatore,
    Messaggio__c: null,
    signedAttachments: [],
    vehicle: signalingData.vehicle,
  })
}

type Props = {}

export function CreateQuickoReportModal({ }: Props) {
  const { showSignalingModal, closeSignalingModal, signalingData } = useAiContext();
  const { accountTypes } = useAccountTypes();
  const [uploadFiles, setUploadFiles] = useState<File[]>([new File([signalingData.pdfBlob], signalingData.fileName)]);
  const { user, userProvvigioniSegnalatore } = useAuth();
  const initialValues: Partial<CreateReportFormData> = buildInitialValues({
    user,
    signalingData
  });
  const { apiClient, handleApiResponse } = useApiClient();
  const [pendingCreation, setPendingCreation] = useState(false);
  const { showError } = useErrorToast();

  useEffect(() => {
    console.debug({ signalingData, userProvvigioniSegnalatore })
  }, [signalingData]);

  const uploadFilesToBucket = async () => {
    if (!uploadFiles.length) {
      return [];
    }

    const filesToUploadList: any[] = uploadFiles.map(file => {
      const ext = file.name.split('.').pop()
      const key = `opportunity_uploads/${randomString(10)}.${ext}`
      return { file, filename: file.name, key, fileType: ext }
    })

    const uploadOps: any[] = filesToUploadList.map(obj => {
      return Storage.put(obj.key, obj.file, {
        contentType: obj.file.type,
        bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
      });
    })

    try {
      await Promise.all(uploadOps)
    } catch (e) {
      showError(e, i18next.t('error_loading_file'))
      throw e;
    }

    return filesToUploadList.map(el => ({ filename: el.filename, key: `public/${el.key}`, fileType: '' }))
  }

  const buildPayload = async (values: CreateReportFormData) => {
    const signedAttachments = await uploadFilesToBucket();

    return ({
      RecordTypeId: values.RecordTypeId || null,
      RecordType: values.RecordType || null,
      FirstName: values.FirstName || null,
      LastName: values.LastName || null,
      Company: values.Company || null,
      Partita_IVA__c: values.Partita_IVA__c || null,
      Codice_Fiscale__c: values.Codice_Fiscale__c || null,
      Email__c: values.Email__c || null,
      Phone: values.Phone || null,
      Marca__c: values.Marca__c || null,
      Modello__c: values.Modello__c || null,
      ID_Vehicle_HMP__c: values.ID_Vehicle_HMP__c || null,
      Provvigioni_Segnalatore__c: values.Provvigioni_Segnalatore__c || 0.00,
      Provvigioni_passive__c: values.Provvigioni_passive__c || 0.00,
      signedAttachments: signedAttachments || [],
      Noleggiatore__c: values.Noleggiatore__c || null,
    })
  }

  const downloadQuickoPdf = () => {
    try {
      const blobUrl = URL.createObjectURL(signalingData.pdfBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = signalingData.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    } catch (error) {
      console.warn(error)
    }
  };

  const handleFormSubmit = async (values, formikHelpers) => {
    setPendingCreation(true);
    const payload = await buildPayload(values);
    handleApiResponse(apiClient.salesforceOpportunityController.createNewReport(payload))
      .then((res) => {
        toaster.success(i18next.t('report_created_successfully'));
        downloadQuickoPdf()
        if (!!res?.warning) toaster.warning(res.warning);
        closeSignalingModal?.()
      })
      .catch(error => {
        showError(error, i18next.t('report_creation_failed'));
      }).finally(() => {
        setPendingCreation(false);
      });
  }

  if (!signalingData) return null;

  return (
    <Modal
      show={showSignalingModal}
      onHide={closeSignalingModal}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title className='display-6'>{i18next.t("create_opportunity")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={createReportValidationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize={true}
          validateOnBlur
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
            setTouched,
          }) => {
            const isPrivato = values?.RecordTypeId === RecordTypeEnum.Privato;
            const isLiberoProfessionista = values?.RecordTypeId === RecordTypeEnum.Libero_Professionista;
            const isAzienda = values?.RecordTypeId === RecordTypeEnum.Azienda;

            return (
              <Form id="report-form" onSubmit={handleSubmit}>
                <Row className="fields-grid">
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('type')}
                      required={!values.RecordTypeId}
                    />
                    <Select
                      menuPosition="fixed"
                      options={accountTypes}
                      isDisabled
                      onChange={(selected) => {
                        setFieldValue(
                          'RecordTypeId',
                          selected ? selected.value : null
                        );
                        setFieldValue(
                          'RecordType',
                          selected ? selected.code : null
                        );
                      }}
                      value={
                        accountTypes?.find(
                          (option) => (values.RecordTypeId ?? '') === option.value
                        ) || null
                      }
                      className={`custom-select ${touched.RecordTypeId && errors.RecordTypeId
                        ? 'select-error'
                        : ''
                        }`}
                    />
                    {touched.RecordTypeId && errors.RecordTypeId ? (
                      <div style={{ color: 'red' }}>{errors.RecordTypeId}</div>
                    ) : null}
                  </Form.Group>
                  {!isAzienda && (
                    <>
                      <Form.Group className="mb-3">
                        <RequiredFormLabel
                          label={i18next.t('first_name')}
                          required={(isPrivato || isLiberoProfessionista) && !values.FirstName}
                        />
                        <Form.Control
                          type="text"
                          name="FirstName"
                          onChange={handleChange}
                          value={values.FirstName ?? ''}
                          isInvalid={!!errors.FirstName && touched.FirstName}
                        />
                        {touched.FirstName && errors.FirstName ? (
                          <div style={{ color: 'red' }}>{errors.FirstName}</div>
                        ) : null}
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <RequiredFormLabel
                          label={i18next.t('last_name')}
                          required={(isPrivato || isLiberoProfessionista) && !values.LastName}
                        />
                        <Form.Control
                          type="text"
                          name="LastName"
                          onChange={handleChange}
                          value={values.LastName ?? ''}
                          isInvalid={!!errors.LastName && touched.LastName}
                        />
                        {touched.LastName && errors.LastName ? (
                          <div style={{ color: 'red' }}>{errors.LastName}</div>
                        ) : null}
                      </Form.Group>
                    </>)}
                  {isAzienda && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('company')}
                        required={isAzienda && !values.Company}
                      />
                      <Form.Control
                        type="text"
                        name="Company"
                        onChange={handleChange}
                        value={values.Company ?? ''}
                        isInvalid={!!errors.Company && touched.Company}
                      />
                      {touched.Company && errors.Company ? (
                        <div style={{ color: 'red' }}>{errors.Company}</div>
                      ) : null}
                    </Form.Group>
                  )}
                  {isPrivato && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('fiscal_code')}
                      />
                      <Form.Control
                        type="text"
                        name="Codice_Fiscale__c"
                        onChange={handleChange}
                        value={values.Codice_Fiscale__c || ''}
                        isInvalid={
                          !!errors.Codice_Fiscale__c &&
                          touched.Codice_Fiscale__c
                        }
                      />
                      {touched.Codice_Fiscale__c && errors.Codice_Fiscale__c ? (
                        <div style={{ color: 'red' }}>
                          {errors.Codice_Fiscale__c}
                        </div>
                      ) : null}
                    </Form.Group>
                  )}
                  {isLiberoProfessionista && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('business_fiscal_code')}
                      />
                      <Form.Control
                        type="text"
                        name="Partita_IVA__c"
                        onChange={handleChange}
                        value={values.Partita_IVA__c || ''}
                        isInvalid={!!errors.Partita_IVA__c && touched.Partita_IVA__c}
                      />
                      {touched.Partita_IVA__c && errors.Partita_IVA__c ? (
                        <div style={{ color: 'red' }}>
                          {errors.Partita_IVA__c}
                        </div>
                      ) : null}
                    </Form.Group>
                  )}
                  {isAzienda && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('business_fiscal_code')}
                      />
                      <Form.Control
                        type="text"
                        name="Partita_IVA__c"
                        onChange={handleChange}
                        value={values.Partita_IVA__c || ''}
                        isInvalid={!!errors.Partita_IVA__c && touched.Partita_IVA__c}
                      />
                      {touched.Partita_IVA__c && errors.Partita_IVA__c ? (
                        <div style={{ color: 'red' }}>
                          {errors.Partita_IVA__c}
                        </div>
                      ) : null}
                    </Form.Group>
                  )}
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('email')}
                      required={!values.Email__c}
                    />
                    <Form.Control
                      type="email"
                      name="Email__c"
                      onChange={handleChange}
                      value={values.Email__c ?? ''}
                      isInvalid={!!errors.Email__c && touched.Email__c}
                    />
                    {touched.Email__c && errors.Email__c ? (
                      <div style={{ color: 'red' }}>{errors.Email__c}</div>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('mobile_phone')}
                      required={!values.Phone}
                    />
                    <Form.Control
                      type="text"
                      name="Phone"
                      onChange={handleChange}
                      value={values.Phone || ''}
                      isInvalid={!!errors.Phone && touched.Phone}
                    />
                    {touched.Phone && errors.Phone ? (
                      <div style={{ color: 'red' }}>{errors.Phone}</div>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('rental_company')}
                      required={false}
                    />
                    <Form.Control
                      type="text"
                      name="Noleggiatore__c"
                      disabled
                      onChange={handleChange}
                      value={values.Noleggiatore__c || ''}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('commissions')}
                      required={false}
                    />
                    <Form.Control
                      type="text"
                      name="Provvigioni_Segnalatore__c"
                      disabled
                      value={values.Provvigioni_Segnalatore__c}
                    />
                  </Form.Group>
                  {/* <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('Provvigioni_passive__c')}
                      required={false}
                    />
                    <Form.Control
                      type="text"
                      name="Provvigioni_passive__c"
                      disabled
                      value={values.Provvigioni_passive__c}
                    />
                  </Form.Group> */}
                </Row>
                <Row className='mb-3 mt-3'>
                  <RequiredFormLabel
                    label={i18next.t('vehicle')}
                    required={!values.Modello__c && !values.Marca__c}
                  />
                  <Card className='mt-2'>
                    <VehicleCardBody data={values.vehicle} />
                  </Card>
                </Row>

                <Row className='mt-3'>
                  <MultiFileUploader
                    showRemove={false}
                    files={uploadFiles}
                    setFiles={setUploadFiles}
                    types={SUPPORTED_FILE_TYPES}
                  ></MultiFileUploader>
                </Row>

                <Row className="mt-4">
                  <Col md={12} className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="me-2"
                      disabled={pendingCreation}
                    >
                      <SpinnerButton
                        busy={pendingCreation}
                        label={i18next.t('save')}
                      />
                    </Button>

                    <Button onClick={closeSignalingModal} disabled={pendingCreation}>
                      <SpinnerButton label={i18next.t('close')} />
                    </Button>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  )
}
